import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import Papa from 'papaparse';
import { filterAvailableInstagramCars } from '../utils/instagramAvailability';
import { enrichCarDetails, findCarByRouteId, resolveCarId } from '../utils/carDetails';

const CarContext = createContext();

export const useCar = () => useContext(CarContext);

const SHEETS_CSV_URL = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vQh8nO4W4vXWZTpCC__BBcu3VICaxTmtX1m-4G1Vzmgsrk0n0_vpuKIb5LAhwtDmO2JY1w5rdeIr0CI/pub?output=csv';

export const CarProvider = ({ children }) => {
    const [cars, setCars] = useState([]);
    const [carsPP, setCarsPP] = useState([]); // Preço fixo / Particular
    const [carsPPB, setCarsPPB] = useState([]); // Brevemente
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const carsCacheRef = useRef({ cars: [], carsPP: [], carsPPB: [] });

    useEffect(() => {
        carsCacheRef.current = { cars, carsPP, carsPPB };
    }, [cars, carsPP, carsPPB]);

    const fetchGoogleSheetsCars = async () => {
        const response = await fetch(`${SHEETS_CSV_URL}&_=${Date.now()}`, {
            cache: 'no-store',
        });
        const csvText = await response.text();
        const results = Papa.parse(csvText, { skipEmptyLines: true });
        const parsedLines = results.data;
        if (parsedLines.length < 2) {
            return [];
        }

        const headers = parsedLines[0].map(h => h.toLowerCase().trim());
        const idIndex = headers.indexOf('id');
        const linkIndex = headers.indexOf('linkinstagram');
        const nomeIndex = headers.indexOf('nome');
        const precoIndex = headers.indexOf('preço') !== -1 ? headers.indexOf('preço') : headers.indexOf('preco');
        const ativoIndex = headers.indexOf('ativo');

        return parsedLines.slice(1)
            .filter(row => {
                const id = String(row[idIndex] ?? '').trim();
                const linkinstagram = String(row[linkIndex] ?? '').trim();
                const nome = String(row[nomeIndex] ?? '').trim();
                const preco = String(row[precoIndex] ?? '').trim();
                return id || linkinstagram || nome || preco;
            })
            .map((row, index) => {
            const id = row[idIndex] || '';
            const linkinstagram = row[linkIndex] || '';
            const nome = row[nomeIndex] || '';
            const preco = row[precoIndex] || '';
            const ativo = ativoIndex >= 0 ? row[ativoIndex] : '';

            let shortcode = null;
            const match = linkinstagram.match(/\/p\/([a-zA-Z0-9_-]+)/);
            if (match) {
                shortcode = match[1];
            }

            const baseCar = {
                id: id,
                titulo: nome,
                preco: preco,
                linkinstagram: linkinstagram,
                thumb_url: shortcode ? `https://www.instagram.com/p/${shortcode}/media/?size=l` : null,
                instagramShortcode: shortcode,
                ativo,
                marca: nome.split(' ')[0] || 'Viatura',
                kms: '0',
                ano: '—',
                combustivel: '—',
                caixa_velocidades: '—',
                vendido: 0,
                brevemente: 0
            };

            return {
                ...baseCar,
                id: resolveCarId(baseCar, index),
            };
        });
    };

    const fetchCarsAU = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await fetchGoogleSheetsCars();
            const available = await filterAvailableInstagramCars(data);
            setCars(available);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchCarsPP = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await fetchGoogleSheetsCars();
            const available = await filterAvailableInstagramCars(data);
            setCarsPP(available);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchCarsPPB = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await fetchGoogleSheetsCars();
            const available = await filterAvailableInstagramCars(data);
            setCarsPPB(available);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchCarDetails = useCallback(async (carId) => {
        try {
            const { cars: cachedCars, carsPP: cachedPP, carsPPB: cachedPPB } = carsCacheRef.current;
            const pools = [...cachedCars, ...cachedPP, ...cachedPPB];

            for (let i = 0; i < pools.length; i += 1) {
                const match = findCarByRouteId([pools[i]], carId);
                if (match) {
                    return { success: true, data: enrichCarDetails(match, i) };
                }
            }

            const rawCars = await fetchGoogleSheetsCars();
            const car = findCarByRouteId(rawCars, carId);

            if (car) {
                const index = rawCars.findIndex(
                    (c, i) =>
                        resolveCarId(c, i) === String(carId) ||
                        String(c.id) === String(carId) ||
                        c.instagramShortcode === carId
                );
                return { success: true, data: enrichCarDetails(car, index >= 0 ? index : 0) };
            }

            return { success: false, error: 'Veículo não encontrado' };
        } catch (err) {
            return { success: false, error: err.message };
        }
    }, []);

    const deleteCar = async (carId) => {
        return { success: false, error: 'A base de dados é apenas de leitura (Google Sheets)' };
    };

    const addCar = async (formData) => {
        return { success: false, error: 'A base de dados é apenas de leitura (Google Sheets)' };
    };

    const editCar = async (carData) => {
        return { success: false, error: 'A base de dados é apenas de leitura (Google Sheets)' };
    };

    const setAsSold = async (carId) => {
        return { success: false, error: 'A base de dados é apenas de leitura (Google Sheets)' };
    };

    const fetchCarPhotos = async (carId) => {
        return { success: true, fotos: [] };
    };

    const deleteCarImage = async (imageId) => {
        return { success: false, error: 'A base de dados é apenas de leitura (Google Sheets)' };
    };

    const setCarThumbnail = async (carId, imageId) => {
        return { success: false, error: 'A base de dados é apenas de leitura (Google Sheets)' };
    };

    const reorderCarImages = async (imagesOrder) => {
        return { success: false, error: 'A base de dados é apenas de leitura (Google Sheets)' };
    };

    const uploadImage = async (formData) => {
        return { success: false, error: 'A base de dados é apenas de leitura (Google Sheets)' };
    };


    return (
        <CarContext.Provider
            value={{
                cars,
                carsPP,
                carsPPB,
                isLoading,
                error,
                fetchCarsAU,
                fetchCarsPP,
                fetchCarsPPB,
                fetchCarDetails,
                addCar,
                deleteCar,
                editCar,
                setAsSold,
                fetchCarPhotos,
                deleteCarImage,
                reorderCarImages,
                setCarThumbnail,
                uploadImage
            }}
        >
            {children}
        </CarContext.Provider>
    );
};
